import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Crown, MapPin, Calendar, Phone, FileText, CheckCircle2, XCircle, Clock, Eye } from 'lucide-react';
import { toast } from 'sonner';
import ApplicationDetailsDialog from '../components/ApplicationDetailsDialog';

const NokmaDashboard = () => {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState(null);
  const [note, setNote] = useState('');
  const [viewing, setViewing] = useState(null);

  const fetchApps = () => {
    setLoading(true);
    api.get('/applications').then(({ data }) => setApps(data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };

  useEffect(() => { fetchApps(); }, []);

  const pending = apps.filter((a) => a.status === 'forwarded_nokma');
  const approved = apps.filter((a) => a.status === 'approved');
  const rejected = apps.filter((a) => a.status === 'rejected');

  const doAction = async () => {
    try {
      if (action === 'approve') {
        await api.put(`/applications/${selected.id}/approve`, { note });
        toast.success('Application approved. Document issued.');
      } else {
        await api.put(`/applications/${selected.id}/reject`, { note });
        toast.error('Application rejected.');
      }
      setSelected(null); setAction(null); setNote('');
      fetchApps();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const ListView = ({ items, showActions }) => (
    <div className="space-y-3">
      {items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">No applications</div>
      ) : items.map((a) => (
        <div key={a.id} className="border border-border rounded-lg p-5 bg-card hover:shadow-warm transition-shadow">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge variant="outline" className="font-mono text-xs">{a.id}</Badge>
                <Badge className="bg-primary/15 text-primary border-primary/20" variant="outline">{a.typeLabel}</Badge>
              </div>
              <div className="font-serif text-lg font-semibold">{a.applicantName}</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{a.applicantMobile}</span>
                <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{a.akhing}</span>
                {a.days && <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{a.days} days</span>}
                {a.purpose && <span className="inline-flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />{a.purpose}</span>}
              </div>
            </div>
            {showActions && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setViewing(a)} className="gap-1.5">
                  <Eye className="h-4 w-4" /> View
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setSelected(a); setAction('reject'); }} className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50">
                  <XCircle className="h-4 w-4" /> Reject
                </Button>
                <Button size="sm" onClick={() => { setSelected(a); setAction('approve'); }} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle2 className="h-4 w-4" /> Approve
                </Button>
              </div>
            )}
            {!showActions && (
              <Button variant="outline" size="sm" onClick={() => setViewing(a)} className="gap-1.5">
                <Eye className="h-4 w-4" /> View
              </Button>
            )}
            {a.status === 'approved' && (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 font-mono">{a.approvalCode}</Badge>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <div className="text-sm text-muted-foreground flex items-center gap-1.5"><Crown className="h-4 w-4 text-primary" /> Nokma Sani · {user.akhing}</div>
          <h1 className="font-serif text-3xl font-bold mt-1">{user.name}</h1>
          <div className="text-sm text-muted-foreground mt-1">Sani: {user.saniName} · Nokma No: {user.nokmaNumber} · Lasker: {user.laskerArea}</div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {[
            { l: 'Pending Approval', v: pending.length, icon: Clock, c: 'text-amber-600' },
            { l: 'Approved', v: approved.length, icon: CheckCircle2, c: 'text-emerald-600' },
            { l: 'Rejected', v: rejected.length, icon: XCircle, c: 'text-rose-600' },
          ].map((s) => (
            <Card key={s.l} className="border-border">
              <CardContent className="pt-6 flex items-center gap-4">
                <div className={`h-12 w-12 rounded-lg bg-secondary flex items-center justify-center ${s.c}`}>
                  <s.icon className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{s.v}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">{s.l}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="border-border">
          <CardHeader><CardTitle className="font-serif">Applications for {user.akhing}</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="text-center py-12 text-muted-foreground">Loading...</div> : (
              <Tabs defaultValue="pending">
                <TabsList>
                  <TabsTrigger value="pending">Pending ({pending.length})</TabsTrigger>
                  <TabsTrigger value="approved">Approved ({approved.length})</TabsTrigger>
                  <TabsTrigger value="rejected">Rejected ({rejected.length})</TabsTrigger>
                </TabsList>
                <TabsContent value="pending" className="mt-4"><ListView items={pending} showActions /></TabsContent>
                <TabsContent value="approved" className="mt-4"><ListView items={approved} /></TabsContent>
                <TabsContent value="rejected" className="mt-4"><ListView items={rejected} /></TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!selected} onOpenChange={(o) => !o && (setSelected(null), setAction(null))}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif">{action === 'approve' ? 'Approve' : 'Reject'} Application</DialogTitle>
              <DialogDescription>
                {selected?.applicantName} · {selected?.typeLabel} · {selected?.akhing}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label>Note (optional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Add a note for the applicant" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setSelected(null); setAction(null); }}>Cancel</Button>
              <Button onClick={doAction} className={action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}>
                Confirm {action === 'approve' ? 'Approval' : 'Rejection'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ApplicationDetailsDialog app={viewing} open={!!viewing} onOpenChange={(o) => !o && setViewing(null)} />
      </main>
      <Footer />
    </div>
  );
};

export default NokmaDashboard;
